import type { SVGProps } from "react";
import { useEffect, useState } from "react";
import type { Tone } from "@/types/tone";
import { coreIconRegistry } from "./core-icon-registry";
import type { IconComponent, IconName } from "./icon-types";
import { type LazyIconName, lazyIconLoaders } from "./lazy-icon-registry";

export type { IconName };

export type IconSize = "xs" | "sm" | "md" | "lg" | "xl" | "xxl";

export type IconTone =
  | Extract<
      Tone,
      "neutral" | "brand" | "warning" | "negative" | "positive" | "informative"
    >
  | "secondary"
  | "inherit";

const toneClassMap: Record<IconTone, string> = {
  neutral: "text-icon-main",
  secondary: "text-icon-secondary",
  brand: "text-primary",
  warning: "text-accessibility-warning",
  negative: "text-negative",
  positive: "text-positive",
  informative: "text-informative",
  inherit: "text-current",
};

const sizeMap: Record<IconSize, string> = {
  xs: "var(--icon-xs)",
  sm: "var(--icon-sm)",
  md: "var(--icon-md)",
  lg: "var(--icon-lg)",
  xl: "var(--icon-xl)",
  xxl: "var(--icon-xxl)",
};

const lazyIconCache = new Map<IconName, IconComponent>();
const pendingLazyIconLoads = new Map<IconName, Promise<IconComponent>>();

type LazyIconState = {
  name: IconName;
  Component: IconComponent;
};

function isCoreIconName(name: IconName): name is keyof typeof coreIconRegistry {
  return name in coreIconRegistry;
}

function loadLazyIcon(name: LazyIconName): Promise<IconComponent> {
  const cachedIcon = lazyIconCache.get(name);
  if (cachedIcon) {
    return Promise.resolve(cachedIcon);
  }

  const pendingLoad = pendingLazyIconLoads.get(name);
  if (pendingLoad) {
    return pendingLoad;
  }

  const loader = lazyIconLoaders[name];
  const load = loader()
    .then((module) => {
      lazyIconCache.set(name, module.default);
      pendingLazyIconLoads.delete(name);
      return module.default;
    })
    .catch((error: unknown) => {
      pendingLazyIconLoads.delete(name);
      throw error;
    });

  pendingLazyIconLoads.set(name, load);
  return load;
}

export interface IconProps
  extends Omit<SVGProps<SVGSVGElement>, "name" | "ref" | "className"> {
  /** The icon to render — must be a key from the icon registry */
  name: IconName;
  /** Icon size. Defaults to "md" (24px). */
  size?: IconSize;
  /** Icon color tone. Defaults to "neutral". */
  tone?: IconTone;
}

/**
 * Icon component backed by the AssetWatch SVG icon registry.
 *
 * Sizes: xs (16px), sm (20px), md (24px), lg (32px), xl (40px), xxl (48px)
 *
 * By default icons are decorative (aria-hidden="true").
 * Provide aria-label for meaningful icons that convey information.
 *
 * @example
 * <Icon name="add-photo" size="md" tone="neutral" aria-hidden />
 * <Icon name="alert-exclamation" size="lg" tone="negative" aria-label="Alert" />
 */
function Icon({
  name,
  size = "md",
  tone = "neutral",
  style,
  "aria-label": ariaLabel,
  "aria-hidden": ariaHidden,
  ...props
}: IconProps) {
  const isDecorative = !ariaLabel;
  const dimension = sizeMap[size];
  const [lazyIconState, setLazyIconState] = useState<LazyIconState | undefined>(
    () => {
      if (isCoreIconName(name)) {
        return undefined;
      }

      const Component = lazyIconCache.get(name);
      return Component ? { name, Component } : undefined;
    },
  );
  const iconProps = {
    className: toneClassMap[tone],
    style: { width: dimension, height: dimension, flexShrink: 0, ...style },
    "aria-hidden": ariaHidden ?? (isDecorative ? true : undefined),
    "aria-label": ariaLabel,
    role: isDecorative ? undefined : "img",
    ...props,
  };

  useEffect(() => {
    if (isCoreIconName(name)) {
      setLazyIconState(undefined);
      return;
    }

    let isCurrent = true;
    const lazyName = name as LazyIconName;
    const cachedIcon = lazyIconCache.get(lazyName);

    if (cachedIcon) {
      setLazyIconState({ name: lazyName, Component: cachedIcon });
      return;
    }

    setLazyIconState(undefined);
    void loadLazyIcon(lazyName)
      .then((LoadedIcon) => {
        if (isCurrent) {
          setLazyIconState({ name: lazyName, Component: LoadedIcon });
        }
      })
      .catch(() => {
        if (isCurrent) {
          setLazyIconState(undefined);
        }
      });

    return () => {
      isCurrent = false;
    };
  }, [name]);

  if (isCoreIconName(name)) {
    const SvgComponent = coreIconRegistry[name];
    return <SvgComponent {...iconProps} />;
  }

  if (lazyIconState?.name === name) {
    const SvgComponent = lazyIconState.Component;
    return <SvgComponent {...iconProps} />;
  }

  return <svg {...iconProps} />;
}

export { Icon };
